import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Clear";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { fetchEmployee } from "../api/employee";
import { addTimeOffRequest } from "../api/timeOffRequest";
import { isUserAuthorizedByServer } from "../api/userAccess";
import AddTimeOffRequestDialog from "../components/AddTimeOffRequestDialog";
import PageLoading from "../components/PageLoading";
import TimeOffRequestTable from "../components/TimeOffRequestTable";
import TimeOffRequestTableMobile from "../components/TimeOffRequestTableMobile";
import UnauthorizedMessage from "../components/UnauthorizedMessage";
import ConfirmationDialog from "../components/fundamentals/ConfirmationDialog";
import StarsBreadcrumbs from "../components/fundamentals/StarsBreadcrumbs";
import SuccessDialog from "../components/fundamentals/SuccessDialog";
import WarningDialog from "../components/fundamentals/WarningDialog";
import { selectStationFilter, selectUserSession, updateStationFilter } from "../slices/userSession/userSessionSlice";
import { logout } from "../util/authentication";
import { getAdminStation, getFormattedPhoneNumber, getMultiStationAdminStationList } from "../util/commonUtils";
import {
  BREADCRUMBS_TITLE,
  CANCEL_WARNING_MESSAGE_ACTION_TYPE,
  FREEFORM_REASON_CHAR_LIMIT,
  HOME_PATH,
  MULTI_STATION_ADMIN_ACCESS,
  STATUS_APPROVED,
  _4HOUR_VAC_TIME_OFF_TYPE,
} from "../util/constants";
import { getUserDataFromEmployeeService } from "../util/employeeDataRetriever";
import {
  setEndDateTime,
  setStartDateTime,
  validateAdminComments,
  validateEmployee,
  validateEmployeeId,
  validatePhoneNumber,
  validateStation,
} from "../util/inputValidation";
import {
  ADMIN_MOBILE_WARNING_MESSAGE,
  ARE_YOU_SURE_WANT_TO_CREATE_THE_TIME_OFF_REQUEST,
  ARE_YOU_SURE_WANT_TO_CREATE_THE_TIME_OFF_REQUESTS,
  ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE,
  END_DATE_IS_REQUIRED,
  SHIFT_TIME_IS_REQUIRED,
  START_DATE_IS_REQUIRED,
  START_DATE_MUST_MATCH_END_DATE,
  START_TIME_IS_REQUIRED,
  END_TIME_IS_REQUIRED,
  TIME_OFF_CONFIRMATION_TITLE,
  TIME_OFF_ELIGIBILITY_ADMIN_WARNING,
  TOR_MULTIPLE_REQUESTS_CREATED_MESSAGE,
  TOR_SINGLE_REQUEST_CREATED_MESSAGE,
  WARNING_MULTIPLE_REQUESTS_HAVE_NO_SHIFT_TIME,
  WARNING_ONE_REQUEST_HAS_NO_SHIFT_TIME,
  WARNING_ALERT_TITLE,
  INVALID_PHONE_NUMBER,
} from "../util/messageConstants";
import { buildTimeOffRequestEventPayload } from "../util/timeOffRequestEventBuilder";
import {
  isDuplicateTimeOffRequest,
  validate24HourWindowForUnknownShiftTimes,
} from "../util/timeOffRequestInputValidation";
import {
  TOR_validateShiftEndDate,
  TOR_validateShiftEndTime,
  TOR_validateShiftStartDate,
  TOR_validateShiftStartTime,
  TOR_validateShiftTwoMonthDeadline,
  validateEmployeeDataForAdminCreateTimeOffRequest,
  validateOwnTimeOffRequestEmployeeId,
} from "../util/timeOffRequestInputValidationAdmin";
import {
  getWOPRequestDateTimes,
  isEADorEDUorSEAD,
  isLWOPTOWOPAllDayRequest,
  isLWOPTOWOPDayOfRequest,
  isLWOPTOWOPRequest,
  isTimeUnknownForShift,
  timeOffRequestDateTimeValidation,
  validateWOPPreferredLeaveTime,
} from "../util/timeOffRequestUtils";
import { getWorkgroupGivenJobCode, getUnionContractGivenJobCode } from "../util/workgroupMapper";
import { isTOREventEligible } from "../api/timeOffRequest";
import { isDSTDay, TODAY, TOMORROW } from "../util/dateHelper";
import { DateTime } from "luxon";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    margin: "auto",
  },
  header: {
    float: "left",
    width: "20%",
  },
  subHeader: {
    paddingTop: "20px",
    paddingBottom: "20px",
    display: "flex",
    justifyContent: "space-between",
    "& .sub-title": {
      fontSize: "20px",
      color: theme.palette.secondary.swaGray,
    },
  },
  topDivider: {
    marginBottom: "20px !important",
    marginLeft: "8% !important",
    marginRight: "10% !important",
  },
  bottomDivider: {
    marginTop: "20px !important",
    marginLeft: "8% !important",
    marginRight: "10% !important",
  },
  pageTitleDesktop: {
    marginLeft: "10%",
  },
  timeOffRequestTable: {
    width: "80%",
    marginLeft: "10%",
  },
  inputContainer: {
    marginLeft: "10% !important",
  },

  //Mobile Version css
  pageTitle: {
    paddingTop: "15px",
    paddingBottom: "5px",
    marginLeft: "0px",
  },

  stationTextField: {
    width: "auto",
    marginTop: "12px",
    marginBottom: "12px",
  },

  subHeaderMobile: {
    display: "grid",
    paddingTop: "10px",
    "& .sub-title": {
      fontSize: "20px",
      color: theme.palette.secondary.swaGray,
      float: "left",
      position: "relative",
      display: "flex",
      paddingBottom: "10px",
    },
  },
  divider: {
    width: "100%",
  },
  button: {
    padding: "8px 22px",
    fontSize: "1.4rem",
    width: "95%",
    margin: "auto",
  },
  noTimeOffRequestMessage: {
    position: "relative",
    margin: "auto",
    top: "5vh",
    color: theme.palette.secondary.swaGray3,
    fontSize: "28px",
    height: "20vh",
  },
  actionButtons: {
    bottom: "0px",
    width: "100%",
  },
  areYouSureDialog: {
    zIndex: "9999 !important",
  },
  employeeInfoText: {
    textAlign: "left",
    marginLeft: "3vw",
    bottom: "3px",
    position: "relative",
  },
  employeeInfoLoading: {
    position: "relative",
    float: "left",
    top: "7px",
    marginLeft: "3vw",
  },
  textInput: {
    textAlign: "left",
    width: "18vw",
    maxWidth: "95vw",
    margin: "auto",
  },
}));

const AdminCreateTimeOffPage = (props) => {
  const classes = useStyles();
  const isAdminPage = true;
  const innerHtmlWidth = useMediaQuery({ maxWidth: 420 });
  const userSession = useSelector(selectUserSession);
  const stationFilter = useSelector(selectStationFilter);
  const dispatch = useDispatch();
  const [station, setStation] = useState("");
  const [stationTouched, setStationTouched] = useState(false);
  const [stationErrors, setStationErrors] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [employeeData, setEmployeeData] = useState(null);
  const [employeeIdErrors, setEmployeeIdErrors] = useState("");
  const [employeeIdSearchLoaded, setEmployeeIdSearchLoaded] = useState(false);
  const [employeeIdSearchTriggered, setEmployeeIdSearchTriggered] = useState(false);
  const [comments, setComments] = useState("");
  const [commentsTouched, setCommentsTouched] = useState(false);
  const [commentsErrors, setCommentsErrors] = useState("");
  const [isAreYouDialogOpen, toggleAreYouSureDialog] = useState(false);
  const [isAddTimeOffDialogOpen, setIsAddTimeOffDialogOpen] = useState(false);
  const [hasUnknownShift, setHasUnknownShift] = useState(false);
  const [isEditTimeOffRequest, setIsEditTimeOffRequest] = useState(false);
  const [isSubmitButtonClicked, setIsSubmitButtonClicked] = useState(false);
  const [isTimeOffRequestSubmitted, setIsTimeOffRequestSubmitted] = useState(false);
  const [isDSTWarningOpen, setIsDSTWarningOpen] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [, updateState] = React.useState();
  const forceUpdate = React.useCallback(() => updateState({}), []);
  const timeOffRequestObj = useRef({});
  const [timeOffTypeError, setTimeOffTypeError] = useState("");
  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [startTimeError, setStartTimeError] = useState("");
  const [endTimeError, setEndTimeError] = useState("");
  const [preferredWOPStartTimeError, setPreferredWOPStartTimeError] = useState("");
  const [preferredWOPEndTimeError, setPreferredWOPEndTimeError] = useState("");
  const [timeOffRequestsList, setTimeOffRequestsList] = useState([]);
  const [warningDialogBox, setWarningDialogBox] = useState(false);
  const [warningDialogBoxMessage, setWarningDialogBoxMessage] = useState("");
  let shiftStartTime = useRef({});
  let shiftEndTime = useRef({});
  let preferredWOPStartTime = useRef({});
  let preferredWOPEndTime = useRef({});
  const [userContract, setUserContract] = useState("");
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);
  const [isTOREventEligibilityWarningDialogOpen, setIsTOREventEligibilityWarningDialogOpen] = useState(false);
  const [asSoonAsPossibleChecked, setAsSoonAsPossibleChecked] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneNumberErrors, setPhoneNumberErrors] = useState("");

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    } else {
      setStation(getAdminStation(props.featureConfig.userStations, userSession, stationFilter));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(userSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(userSession.isAdmin);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };
  const sortRequest = (list) => {
    list.sort((a, b) => a.shiftStartDateTime.ts - b.shiftStartDateTime.ts);
    list.sort((a, b) =>
      a.shiftStartDateTime.ts === b.shiftStartDateTime.ts ? a.timeOffType.localeCompare(b.timeOffType) : 1
    );
    return list;
  };
  const fetchEmployeeData = (eid) => {
    setEmployeeIdSearchLoaded(false);
    setEmployeeIdSearchTriggered(true);
    setEmployeeIdErrors("");
    setEmployeeData(null);
    fetchEmployee(eid)
      .then((res) => res.json())
      .then(async (result) => {
        if (!result.error && !result.message) {
          let employeeData = await getUserDataFromEmployeeService(result);
          let employeeDataErrors = validateEmployeeDataForAdminCreateTimeOffRequest(employeeData, station);
          if (employeeDataErrors === "") {
            // data fields are populated, now do deeper validation
            employeeDataErrors = validateEmployee(
              employeeData.eid,
              employeeData.firstName,
              employeeData.lastName,
              employeeData.activeStation,
              getWorkgroupGivenJobCode(employeeData.jobCode)
            );
          }
          if (employeeDataErrors !== "") {
            setEmployeeIdErrors(employeeDataErrors);
            setEmployeeIdSearchLoaded(true);
          }
          let userContract = getUnionContractGivenJobCode(employeeData.jobCode);
          setEmployeeData(employeeData);
          setUserContract(userContract);
          setEmployeeIdSearchLoaded(true);
        } else {
          setEmployeeIdErrors("Invalid Employee ID");
          setEmployeeIdSearchLoaded(true);
        }
      });
  };

  const checkForEmptyDates = () => {
    if (!timeOffRequestObj.current.shiftStartDate || !timeOffRequestObj.current.shiftEndDate) {
      const startDateValidationMessage = TOR_validateShiftStartDate(
        null,
        station,
        timeOffRequestObj.current.timeOffType,
        userContract
      );
      const endDateValidationMessage = TOR_validateShiftEndDate(
        null,
        null,
        station,
        timeOffRequestObj.current.timeOffType,
        userContract
      );
      if (!!endDateValidationMessage || !!startDateValidationMessage) {
        return true;
      } else {
        return false;
      }
    }
  };

  const addTimeToDate = () => {
    if (
      !checkForEmptyDates() &&
      shiftStartTime.current["shiftStartHour"] !== undefined &&
      shiftStartTime.current["shiftStartMin"] !== undefined &&
      shiftEndTime.current["shiftEndHour"] !== undefined &&
      shiftEndTime.current["shiftEndMin"] !== undefined
    ) {
      let endTimeError = TOR_validateShiftEndTime(
        timeOffRequestObj.current.timeOffType,
        timeOffRequestObj.current["shiftStartDate"],
        timeOffRequestObj.current["shiftEndDate"],
        shiftStartTime.current["shiftStartHour"],
        shiftStartTime.current["shiftStartMin"],
        shiftEndTime.current["shiftEndHour"],
        shiftEndTime.current["shiftEndMin"],
        station
      );
      setEndTimeError(endTimeError);

      if (!endTimeError) {
        let timeDifferenceValidations = timeOffRequestDateTimeValidation(
          timeOffRequestObj.current["shiftStartDate"],
          timeOffRequestObj.current["shiftEndDate"],
          shiftStartTime.current["shiftStartHour"],
          shiftStartTime.current["shiftStartMin"],
          shiftEndTime.current["shiftEndHour"],
          shiftEndTime.current["shiftEndMin"],
          station,
          timeOffRequestObj.current.timeOffType,
          userContract
        );
        if (!!timeDifferenceValidations) {
          setEndTimeError(timeDifferenceValidations);
          setStartTimeError("");
        } else {
          setEndTimeError("");
          setStartTimeError("");
        }
      }
    }
  };

  const isValidTimeOff = () => {
    let isError = false;
    let deadLineErrors = "";
    let unknown24hrsShiftDateTimeError = "";
    let timeOffType = timeOffRequestObj.current["timeOffType"];
    let startDate = !!timeOffRequestObj.current["shiftStartDate"] ? timeOffRequestObj.current["shiftStartDate"] : null;
    let endDate = !!timeOffRequestObj.current["shiftEndDate"] ? timeOffRequestObj.current["shiftEndDate"] : null;
    let shiftStartHour =
      shiftStartTime.current["shiftStartHour"] !== undefined ? shiftStartTime.current["shiftStartHour"] : "";
    let shiftStartMin =
      shiftStartTime.current["shiftStartMin"] !== undefined ? shiftStartTime.current["shiftStartMin"] : "";

    let shiftEndHour = shiftEndTime.current["shiftEndHour"] !== undefined ? shiftEndTime.current["shiftEndHour"] : "";
    let shiftEndMin = shiftEndTime.current["shiftEndMin"] !== undefined ? shiftEndTime.current["shiftEndMin"] : "";
    let shiftStartDateError = TOR_validateShiftStartDate(startDate, station, timeOffType, userContract);
    let shiftEndDateError = TOR_validateShiftEndDate(startDate, endDate, station, timeOffType, userContract);
    let shiftStartTimeError = TOR_validateShiftStartTime(
      timeOffType,
      startDate,
      shiftStartHour,
      shiftStartMin,
      shiftEndHour,
      shiftEndMin,
      station
    );
    let shiftEndTimeError = TOR_validateShiftEndTime(
      timeOffType,
      startDate,
      endDate,
      shiftStartHour,
      shiftStartMin,
      shiftEndHour,
      shiftEndMin
    );
    if (
      startDate !== null &&
      endDate !== null &&
      shiftStartHour !== "" &&
      shiftStartMin !== "" &&
      shiftEndHour !== null &&
      shiftEndMin !== null
    ) {
      deadLineErrors = timeOffRequestDateTimeValidation(
        startDate,
        endDate,
        shiftStartHour,
        shiftStartMin,
        shiftEndHour,
        shiftEndMin,
        station,
        timeOffType,
        userContract
      );
      setEndTimeError(deadLineErrors);
    }
    if (
      isTimeUnknownForShift(shiftStartHour, shiftStartMin, shiftEndHour, shiftEndMin) &&
      isEADorEDUorSEAD(timeOffRequestObj.current.timeOffType)
    ) {
      unknown24hrsShiftDateTimeError = validate24HourWindowForUnknownShiftTimes(startDate, endDate, station);
      if (unknown24hrsShiftDateTimeError === SHIFT_TIME_IS_REQUIRED) {
        setEndTimeError(unknown24hrsShiftDateTimeError);
        setStartTimeError(unknown24hrsShiftDateTimeError);
      } else if (unknown24hrsShiftDateTimeError === START_DATE_MUST_MATCH_END_DATE) {
        setEndDateError(unknown24hrsShiftDateTimeError);
      }
    }

    if (isLWOPTOWOPRequest(timeOffRequestObj.current.timeOffType)) {
      if (phoneNumberErrors !== "") {
        isError = true;
      }

      if (!(isLWOPTOWOPDayOfRequest(timeOffRequestObj.current.timeOffType) && asSoonAsPossibleChecked)) {
        if (
          preferredWOPStartTime.current.preferredWOPStartHour === undefined ||
          preferredWOPStartTime.current.preferredWOPStartMin === undefined
        ) {
          setPreferredWOPStartTimeError(START_TIME_IS_REQUIRED);
          isError = true;
        }
        if (
          preferredWOPEndTime.current.preferredWOPEndHour === undefined ||
          preferredWOPEndTime.current.preferredWOPEndMin === undefined
        ) {
          setPreferredWOPEndTimeError(END_TIME_IS_REQUIRED);
          isError = true;
        }
        if (shiftStartHour === "" || shiftStartMin === "" || shiftEndHour === "" || shiftEndMin === "") {
          isError = true;
        }

        if (!isError && !isLWOPTOWOPAllDayRequest(timeOffRequestObj.current.timeOffType)) {
          startDate = startDate.set({ hour: shiftStartHour, minute: shiftStartMin });
          endDate = endDate.set({ hour: shiftEndHour, minute: shiftEndMin });
          const error = validateWOPPreferredLeaveTime(
            startDate,
            endDate,
            preferredWOPStartTime.current,
            preferredWOPEndTime.current,
            asSoonAsPossibleChecked,
            station,
            isAdminPage
          );
          setPreferredWOPStartTimeError(error);
          setPreferredWOPEndTimeError(error);

          if (error !== "") {
            isError = true;
          }
        }
      }
    }

    if (!!shiftStartDateError) setStartDateError(shiftStartDateError);
    if (!!shiftEndDateError) setEndDateError(shiftEndDateError);
    if (!!shiftStartTimeError) setStartTimeError(shiftStartTimeError);
    if (!!shiftEndTimeError) setEndTimeError(shiftEndTimeError);

    if (
      !!shiftStartDateError ||
      !!shiftEndDateError ||
      !!shiftStartTimeError ||
      !!shiftEndTimeError ||
      !!deadLineErrors ||
      !!unknown24hrsShiftDateTimeError
    ) {
      isError = true;
      return isError;
    } else {
      return isError;
    }
  };

  const selectTimeOffRequestTimeOff = (selectedTimeOffType) => {
    timeOffRequestObj.current["timeOffType"] = selectedTimeOffType.target.value;

    if (selectedTimeOffType.target.value === _4HOUR_VAC_TIME_OFF_TYPE) {
      setTimeFor4HrVacTimeOffType(shiftStartTime.current["shiftStartHour"]);
    }

    if (isLWOPTOWOPRequest(selectedTimeOffType.target.value)) {
      setDateForWOPTimeOffType(selectedTimeOffType.target.value);
    }

    forceUpdate();
    setStartDateError("");
    setEndDateError("");
    setStartTimeError("");
    setEndTimeError("");
    setTimeOffTypeError("");
    setPreferredWOPStartTimeError("");
    setPreferredWOPEndTimeError("");
    setPhoneNumberErrors("");
  };

  const clearBottomForm = () => {
    // TODO: As form state is added we need to make sure this gets updated
    setEmployeeIdErrors("");
    setEmployeeIdSearchLoaded(false);
    setEmployeeIdSearchTriggered(false);
    setEmployeeId("");
    setEmployeeData(null);
    setComments("");
    setCommentsTouched(false);
    setCommentsErrors("");
  };

  const handleStationChange = (selectedStation) => {
    clearBottomForm();
    setStation(selectedStation.target.value);
    dispatch(updateStationFilter(selectedStation.target.value));
  };

  const handleStationBlur = (event) => {
    setStationErrors(validateStation(event.target.value));
    setStationTouched(true);
  };

  const handleEmployeeIdChange = (eid) => {
    if (employeeData) {
      setEmployeeData(null);
    }
    setEmployeeId(eid.target.value.toLowerCase());
  };

  const handleEmployeeDataClear = () => {
    setEmployeeId("");
    setEmployeeData(null);
    let errors = validateEmployeeId("");
    if (errors !== "") {
      setEmployeeIdErrors(errors);
    }
  };

  const handleEmployeeIdBlur = (event) => {
    let trimEmpId = event.target.value.trim();
    let errors = validateEmployeeId(trimEmpId) + validateOwnTimeOffRequestEmployeeId(trimEmpId, userSession.eid);
    if (employeeIdErrors === "" || errors !== "") {
      setEmployeeIdErrors(errors);
    }
    if (errors === "") {
      if (!employeeData || trimEmpId !== employeeData.eid) {
        fetchEmployeeData(trimEmpId);
      }
    }
  };

  const handleEmployeeIdEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleEmployeeIdBlur(event);
    }
  };

  const handleCommentsChange = (comments) => {
    setComments(comments.target.value);
    setCommentsTouched(true);
  };

  const handleCommentsBlur = (event) => {
    setCommentsTouched(true);
    setCommentsErrors(validateAdminComments(event.target.value));
  };

  const handleCommentsClear = () => {
    setComments("");
    setCommentsErrors(validateAdminComments(""));
  };

  const handleShiftStartDateChange = (selectedStartDate) => {
    setValidationError("", "");
    const _timeOffRequestObj = { ...timeOffRequestObj.current };
    _timeOffRequestObj["shiftStartDate"] = selectedStartDate;
    _timeOffRequestObj["shiftEndDate"] = selectedStartDate;

    // Logic to set the end date for LWOP/TOWOP to allowed day if start date is in the past
    if (isLWOPTOWOPRequest(timeOffRequestObj.current.timeOffType)) {
      if (isLWOPTOWOPAllDayRequest(timeOffRequestObj.current.timeOffType) && selectedStartDate < TODAY(station)) {
        _timeOffRequestObj["shiftEndDate"] = selectedStartDate.plus({ days: 1 });
      } else if (selectedStartDate <= TODAY(station).plus({ days: -1 }))
        _timeOffRequestObj["shiftEndDate"] = selectedStartDate.plus({ days: 1 });
    }

    timeOffRequestObj.current = _timeOffRequestObj;
    forceUpdate();
    let startDateValidationMessage = TOR_validateShiftStartDate(
      timeOffRequestObj.current.shiftStartDate,
      station,
      timeOffRequestObj.current.timeOffType,
      userContract
    );

    if (!!startDateValidationMessage) {
      setEndDateError(startDateValidationMessage);
    } else {
      setEndDateError("");
    }

    if (startDateError === START_DATE_IS_REQUIRED) {
      setStartDateError("");
    }

    if (
      shiftStartTime.current["shiftStartHour"] !== undefined &&
      shiftStartTime.current["shiftStartMin"] !== undefined &&
      startTimeError === START_TIME_IS_REQUIRED
    ) {
      setStartTimeError("");
    }

    if (
      timeOffRequestObj.current["shiftStartDate"] &&
      timeOffRequestObj.current["shiftEndDate"] &&
      (isDSTDay(timeOffRequestObj.current["shiftStartDate"]) || isDSTDay(timeOffRequestObj.current["shiftEndDate"]))
    ) {
      setIsDSTWarningOpen(true);
    } else {
      setIsDSTWarningOpen(false);
    }

    addTimeToDate();
  };

  const handleShiftStartDateHoursChange = (selectedStartHour) => {
    setValidationError("", "");
    shiftStartTime.current["shiftStartHour"] = selectedStartHour.target.value;

    // UX for 4-Hour VAC input
    if (timeOffRequestObj.current.timeOffType === _4HOUR_VAC_TIME_OFF_TYPE) {
      setTimeFor4HrVacTimeOffType(selectedStartHour.target.value);
    }

    if (!timeOffRequestObj.current.shiftStartDate) {
      let startDateValidationMessage = TOR_validateShiftStartDate(
        null,
        station,
        timeOffRequestObj.current.timeOffType,
        userContract
      );
      setStartDateError(startDateValidationMessage);
    }

    if (shiftStartTime.current["shiftStartMin"] !== undefined) {
      setStartTimeError("");
    }

    addTimeToDate();
    forceUpdate();
  };

  const setTimeFor4HrVacTimeOffType = (shiftStartHour) => {
    let shiftEndDateAndHourList = getAutomatedEndDateAndEndHourValueFor4HrVacTimeOffType(shiftStartHour);
    if (shiftEndDateAndHourList[0] !== undefined) {
      timeOffRequestObj.current["shiftEndDate"] = shiftEndDateAndHourList[0];
    }
    shiftEndTime.current["shiftEndHour"] = !!shiftEndDateAndHourList[1] ? shiftEndDateAndHourList[1] : undefined;
  };

  const setDateForWOPTimeOffType = (timeOffType) => {
    const dateToSet = isLWOPTOWOPDayOfRequest(timeOffType) ? TODAY(station) : TOMORROW(station);

    timeOffRequestObj.current["shiftEndDate"] = dateToSet;
    timeOffRequestObj.current["shiftStartDate"] = dateToSet;

    if (isLWOPTOWOPAllDayRequest(timeOffType)) {
      preferredWOPStartTime.current.preferredWOPStartHour = 0;
      preferredWOPStartTime.current.preferredWOPStartMin = 0;
      preferredWOPEndTime.current.preferredWOPEndHour = 0;
      preferredWOPEndTime.current.preferredWOPEndMin = 0;
    }

    addTimeToDate();
  };

  const getAutomatedEndDateAndEndHourValueFor4HrVacTimeOffType = (hrInput) => {
    const VAC_SHIFT_TIME_HOURS = 4;
    let automatedDateValue;
    let automatedHourValue = hrInput + VAC_SHIFT_TIME_HOURS;

    // Crossing midnight handler
    if (automatedHourValue > 23) {
      let remainingHours = hrInput + VAC_SHIFT_TIME_HOURS - 23;
      automatedHourValue = remainingHours - 1;

      if (timeOffRequestObj.current.shiftStartDate !== undefined) {
        automatedDateValue = timeOffRequestObj.current.shiftStartDate.plus({ days: 1 });
      }

      return [automatedDateValue, automatedHourValue];
    }

    return [timeOffRequestObj.current.shiftStartDate, automatedHourValue];
  };

  const handleShiftStartDateMinChange = (selectedStartMin) => {
    setValidationError("", "");
    shiftStartTime.current["shiftStartMin"] = selectedStartMin.target.value;

    // UX for 4-Hour VAC input
    if (timeOffRequestObj.current.timeOffType === _4HOUR_VAC_TIME_OFF_TYPE) {
      shiftEndTime.current["shiftEndMin"] = selectedStartMin.target.value;
    }

    if (!timeOffRequestObj.current.shiftStartDate) {
      let startDateValidationMessage = TOR_validateShiftStartDate(
        null,
        station,
        timeOffRequestObj.current.timeOffType,
        userContract
      );
      setStartDateError(startDateValidationMessage);
    }

    if (shiftStartTime.current["shiftStartHour"] !== undefined) {
      setStartTimeError("");
    }

    addTimeToDate();
    forceUpdate();
  };

  const handleShiftEndDateChange = (selectedEndDate) => {
    setValidationError("", "");
    let endDateValidation = "";
    const _timeOffRequestObj = { ...timeOffRequestObj.current };
    _timeOffRequestObj["shiftEndDate"] = selectedEndDate;
    timeOffRequestObj.current = _timeOffRequestObj;
    forceUpdate();
    if (!!timeOffRequestObj.current.shiftStartDate) {
      endDateValidation = TOR_validateShiftEndDate(
        timeOffRequestObj.current.shiftStartDate,
        timeOffRequestObj.current.shiftEndDate,
        station,
        timeOffRequestObj.current.timeOffType,
        userContract
      );
      setEndDateError(endDateValidation);
    }

    if (!timeOffRequestObj.current.shiftStartDate) {
      let startDateError = TOR_validateShiftStartDate(
        null,
        station,
        timeOffRequestObj.current.timeOffType,
        userContract
      );
      setStartDateError(startDateError);
      setEndDateError("");
    }

    if (
      timeOffRequestObj.current["shiftStartDate"] &&
      timeOffRequestObj.current["shiftEndDate"] &&
      (isDSTDay(timeOffRequestObj.current["shiftStartDate"]) || isDSTDay(timeOffRequestObj.current["shiftEndDate"]))
    ) {
      setIsDSTWarningOpen(true);
    } else {
      setIsDSTWarningOpen(false);
    }

    addTimeToDate();
  };

  const handleShiftEndDateHoursChange = (selectedEndHour) => {
    setValidationError("", "");
    shiftEndTime.current["shiftEndHour"] = selectedEndHour.target.value;
    if (!!checkForEmptyDates()) {
      if (!timeOffRequestObj.current.shiftStartDate) {
        setStartDateError(START_DATE_IS_REQUIRED);
      }
      if (!timeOffRequestObj.current.shiftEndDate) {
        setEndDateError(END_DATE_IS_REQUIRED);
      }
    }
    if (shiftEndTime.current["shiftEndMin"] !== undefined) {
      setEndTimeError("");
    }
    addTimeToDate();
    forceUpdate();
  };

  const handleShiftEndDateMinChange = (selectedEndMin) => {
    setValidationError("", "");
    shiftEndTime.current["shiftEndMin"] = selectedEndMin.target.value;
    if (!!checkForEmptyDates()) {
      if (!timeOffRequestObj.current.shiftStartDate) {
        setStartDateError(START_DATE_IS_REQUIRED);
      }
      if (!timeOffRequestObj.current.shiftEndDate) {
        setEndDateError(END_DATE_IS_REQUIRED);
      }
    }
    if (shiftEndTime.current["shiftEndHour"] !== undefined) {
      setEndTimeError("");
    }
    addTimeToDate();
    forceUpdate();
  };

  const handlePreferredWOPStartHourChange = (selectedStartHour) => {
    setValidationError("", "");
    setPreferredWOPStartTimeError("");
    preferredWOPStartTime.current.preferredWOPStartHour = selectedStartHour.target.value;

    if (preferredWOPStartTime.current.preferredWOPStartMin) {
      setPreferredWOPStartTimeError("");
    }

    if (
      preferredWOPEndTime.current.preferredWOPEndHour !== undefined &&
      preferredWOPEndTime.current.preferredWOPEndMin !== undefined
    ) {
      setPreferredWOPEndTimeError("");
    }

    forceUpdate();
  };

  const handlePreferredWOPStartMinChange = (selectedStartMin) => {
    setValidationError("", "");
    setPreferredWOPStartTimeError("");
    preferredWOPStartTime.current.preferredWOPStartMin = selectedStartMin.target.value;

    if (
      !preferredWOPStartTime.current.preferredWOPStartHour &&
      preferredWOPStartTime.current.preferredWOPStartHour !== 0
    ) {
      setPreferredWOPStartTimeError(START_TIME_IS_REQUIRED);
    }

    if (
      preferredWOPEndTime.current.preferredWOPEndHour !== undefined &&
      preferredWOPEndTime.current.preferredWOPEndMin !== undefined
    ) {
      setPreferredWOPEndTimeError("");
    }

    forceUpdate();
  };

  const handlePreferredWOPEndHourChange = (selectedEndHour) => {
    setValidationError("", "");
    setPreferredWOPEndTimeError("");
    preferredWOPEndTime.current.preferredWOPEndHour = selectedEndHour.target.value;

    if (preferredWOPEndTime.current.preferredWOPEndMin) {
      setPreferredWOPEndTimeError("");
    }

    if (
      preferredWOPStartTime.current.preferredWOPStartHour !== undefined &&
      preferredWOPStartTime.current.preferredWOPStartMin !== undefined
    ) {
      setPreferredWOPStartTimeError("");
    }

    forceUpdate();
  };

  const handlePreferredWOPEndMinChange = (selectedEndMin) => {
    setValidationError("", "");
    setPreferredWOPEndTimeError("");
    preferredWOPEndTime.current.preferredWOPEndMin = selectedEndMin.target.value;

    if (!preferredWOPEndTime.current.preferredWOPEndHour && preferredWOPEndTime.current.preferredWOPEndHour !== 0) {
      setPreferredWOPEndTimeError(END_TIME_IS_REQUIRED);
    }

    if (
      preferredWOPStartTime.current.preferredWOPStartHour !== undefined &&
      preferredWOPStartTime.current.preferredWOPStartMin !== undefined
    ) {
      setPreferredWOPStartTimeError("");
    }

    forceUpdate();
  };

  const handlePhoneNumberChange = (event) => {
    setPhoneNumber(event.target.value);
  };
  const handlePhoneNumberBlur = () => {
    validateContactNumber();
  };

  const validateContactNumber = () => {
    const phoneNumberValidation = validatePhoneNumber(phoneNumber, "", INVALID_PHONE_NUMBER);
    setPhoneNumberErrors(phoneNumberValidation);
    if (phoneNumberValidation === "" && phoneNumber !== "") {
      const formattedPhoneNumber = getFormattedPhoneNumber(phoneNumber);
      setPhoneNumber(formattedPhoneNumber);
    }
  };

  const handleAsSoonAsPossibleChecked = (event) => {
    setAsSoonAsPossibleChecked(event.target.checked);
    if (event.target.checked) {
      setPreferredWOPStartTimeError("");
      setPreferredWOPEndTimeError("");
      preferredWOPStartTime.current.preferredWOPStartHour = 0;
      preferredWOPStartTime.current.preferredWOPStartMin = 0;
      preferredWOPEndTime.current.preferredWOPEndHour = 0;
      preferredWOPEndTime.current.preferredWOPEndMin = 0;
    }
  };

  const handleOnSave = () => {
    const shiftExistsError = "Shift has already been added";
    if (!!timeOffRequestObj.current.timeOffType) {
      if (!isValidTimeOff()) {
        let timeOffRequest = {};
        let _timeOffRequestsList = [...timeOffRequestsList];
        const timeOffType = timeOffRequestObj.current["timeOffType"];
        const shiftStartDateTime = setStartDateTime(
          timeOffRequestObj.current["shiftStartDate"],
          shiftStartTime.current["shiftStartHour"],
          shiftStartTime.current["shiftStartMin"],
          station
        );
        const shiftEndDateTime = setEndDateTime(
          timeOffRequestObj.current["shiftEndDate"],
          shiftEndTime.current["shiftEndHour"],
          shiftEndTime.current["shiftEndMin"],
          station
        );

        const addWOPDataToTOR = () => {
          if (isLWOPTOWOPAllDayRequest(timeOffType)) {
            timeOffRequest.preferredWOPStartTime = shiftStartDateTime.toISO();
            timeOffRequest.preferredWOPEndTime = shiftEndDateTime.toISO();
          } else {
            const requestDateTimes = getWOPRequestDateTimes(
              shiftStartDateTime,
              shiftEndDateTime,
              preferredWOPStartTime.current.preferredWOPStartHour,
              preferredWOPStartTime.current.preferredWOPStartMin,
              preferredWOPEndTime.current.preferredWOPEndHour,
              preferredWOPEndTime.current.preferredWOPEndMin,
              asSoonAsPossibleChecked
            );
            timeOffRequest.preferredWOPStartTime = requestDateTimes.requestStart.toISO();
            timeOffRequest.preferredWOPEndTime = requestDateTimes.requestEnd.toISO();
          }
          timeOffRequest.wopASAP = asSoonAsPossibleChecked;
          timeOffRequest.phoneNumber = phoneNumber;
        };

        if (!timeOffRequestObj.current["id"]) {
          timeOffRequest = {
            id: Math.random().toString(36).slice(2),
            timeOffType,
            shiftStartDateTime,
            shiftEndDateTime,
          };
          if (isLWOPTOWOPRequest(timeOffType)) {
            addWOPDataToTOR();
          }
          if (isDuplicateTimeOffRequest(_timeOffRequestsList, timeOffRequest)) {
            setValidationError(shiftExistsError, "");
          } else {
            _timeOffRequestsList.push(timeOffRequest);
            const sortedRequestList = sortRequest(_timeOffRequestsList);
            setTimeOffRequestsList(sortedRequestList);
            setIsAddTimeOffDialogOpen(false);
            handleCloseAddRequestModal();
          }
        } else {
          const id = timeOffRequestObj.current["id"];
          timeOffRequest = { id, timeOffType, shiftStartDateTime, shiftEndDateTime };
          if (isLWOPTOWOPRequest(timeOffType)) {
            addWOPDataToTOR();
          }
          if (isDuplicateTimeOffRequest(_timeOffRequestsList, timeOffRequest)) {
            setValidationError(shiftExistsError, "");
          } else {
            const updatedTimeOffRequest = _timeOffRequestsList.map((timeOfRequest) =>
              timeOfRequest.id === timeOffRequestObj.current["id"]
                ? { ...timeOfRequest, ...timeOffRequest }
                : timeOfRequest
            );
            const sortedRequestList = sortRequest(updatedTimeOffRequest);
            setTimeOffRequestsList(sortedRequestList);
            handleCloseAddRequestModal();
          }
        }
      }
    } else {
      setTimeOffTypeError("Time Off Type is required");
    }
  };

  const setValidationError = (message, replacementMessage) => {
    setStartDateError(replacementMessage);
    setStartTimeError(replacementMessage);
    setEndDateError(replacementMessage);
    setEndTimeError(message);
  };

  const handleOnEditTimeOffRequest = (timeOffRequest) => {
    setIsEditTimeOffRequest(true);
    timeOffRequestObj.current["id"] = timeOffRequest.id;
    timeOffRequestObj.current["timeOffType"] = timeOffRequest.timeOffType;
    timeOffRequestObj.current["shiftStartDate"] = timeOffRequest.shiftStartDateTime;
    timeOffRequestObj.current["shiftEndDate"] = timeOffRequest.shiftEndDateTime;
    shiftStartTime.current["shiftStartHour"] = timeOffRequest.shiftStartDateTime.hour;
    shiftStartTime.current["shiftStartMin"] = timeOffRequest.shiftStartDateTime.minute;
    shiftEndTime.current["shiftEndHour"] = timeOffRequest.shiftEndDateTime.hour;
    shiftEndTime.current["shiftEndMin"] = timeOffRequest.shiftEndDateTime.minute;
    if (isLWOPTOWOPRequest(timeOffRequest.timeOffType)) {
      preferredWOPStartTime.current["preferredWOPStartHour"] = DateTime.fromISO(timeOffRequest.preferredWOPStartTime, {
        setZone: true,
      }).hour;
      preferredWOPStartTime.current["preferredWOPStartMin"] = DateTime.fromISO(timeOffRequest.preferredWOPStartTime, {
        setZone: true,
      }).minute;
      preferredWOPEndTime.current["preferredWOPEndHour"] = DateTime.fromISO(timeOffRequest.preferredWOPEndTime, {
        setZone: true,
      }).hour;
      preferredWOPEndTime.current["preferredWOPEndMin"] = DateTime.fromISO(timeOffRequest.preferredWOPEndTime, {
        setZone: true,
      }).minute;
      setAsSoonAsPossibleChecked(timeOffRequest.wopASAP);
      setPhoneNumber(timeOffRequest.phoneNumber);
    }
  };

  const handleCloseAddRequestModal = () => {
    setIsAddTimeOffDialogOpen(false);
    timeOffRequestObj.current = {};
    shiftStartTime.current = {};
    shiftEndTime.current = {};
    preferredWOPStartTime.current = {};
    preferredWOPEndTime.current = {};
    setPreferredWOPStartTimeError("");
    setPreferredWOPEndTimeError("");
    setAsSoonAsPossibleChecked(false);
    setPhoneNumber("");
    setPhoneNumberErrors("");
    setStartDateError("");
    setEndDateError("");
    setStartTimeError("");
    setEndTimeError("");
    setTimeOffTypeError("");
    setIsDSTWarningOpen(false);
    setIsEditTimeOffRequest(false);
  };

  const handleOnRemove = () => {
    let trimmedTimeOffRequests = timeOffRequestsList.filter(
      (timeOffRequest) => timeOffRequest.id !== timeOffRequestObj.current.id
    );
    setTimeOffRequestsList(trimmedTimeOffRequests);
    handleCloseAddRequestModal();
    toggleAreYouSureDialog(false);
  };

  const handleOnYesButtonClick = () => {
    if (isEditTimeOffRequest) {
      handleOnRemove();
    }
    if (isSubmitButtonClicked) {
      handleOnSubmit();
    }
  };

  const buildWOPObject = (timeOffRequest) => {
    return {
      preferredWOPStart: timeOffRequest.preferredWOPStartTime,
      preferredWOPEnd: timeOffRequest.preferredWOPEndTime,
      wopASAP: timeOffRequest.wopASAP,
      phoneNumber: timeOffRequest.phoneNumber,
      approvedWOPStart: isLWOPTOWOPAllDayRequest(timeOffRequest.timeOffType)
        ? timeOffRequest.shiftStartDateTime.toISO()
        : timeOffRequest.preferredWOPStartTime,
      approvedWOPEnd: isLWOPTOWOPAllDayRequest(timeOffRequest.timeOffType)
        ? timeOffRequest.shiftEndDateTime.toISO()
        : timeOffRequest.preferredWOPEndTime,
    };
  };

  const prepareTimeOffRequestObject = () => {
    const getConvertedTimeOffRequests = () => {
      let timeOffRequestList = [];
      for (let timeOffRequest of timeOffRequestsList) {
        let timeOffRequestObj = {};
        timeOffRequestObj.timeOffRequestType = timeOffRequest.timeOffType;
        timeOffRequestObj.shiftStart = timeOffRequest.shiftStartDateTime;
        timeOffRequestObj.shiftEnd = timeOffRequest.shiftEndDateTime;
        if (isLWOPTOWOPRequest(timeOffRequest.timeOffType)) {
          timeOffRequestObj = {
            ...timeOffRequestObj,
            ...buildWOPObject(timeOffRequest),
          };
        }
        timeOffRequestList.push(timeOffRequestObj);
      }
      return timeOffRequestList;
    };

    const timeOffRequestObj = {
      station: station,
      workgroup: getWorkgroupGivenJobCode(employeeData.jobCode),
      empId: employeeData.eid,
      empFirstName: employeeData.firstName,
      empLastName: employeeData.lastName,
      empWorkgroup: getWorkgroupGivenJobCode(employeeData.jobCode),
      reasonComments: comments,
      isAdditionalApproved: false,
      isReinstated: false,
      isAdminCreated: true,
      requestingShifts: getConvertedTimeOffRequests(),
    };

    return timeOffRequestObj;
  };

  const handleOnSubmit = async () => {
    setIsTimeOffRequestSubmitted(true);

    const timeOffRequestObject = prepareTimeOffRequestObject();
    const timeOffRequestPayload = buildTimeOffRequestEventPayload(STATUS_APPROVED, timeOffRequestObject, userSession);

    const isTOREligible = await isTOREventEligible(timeOffRequestPayload);

    if (!isTOREligible) {
      toggleAreYouSureDialog(false);
      setIsSubmitButtonClicked(false);
      setIsTimeOffRequestSubmitted(false);
      setIsTOREventEligibilityWarningDialogOpen(true);
      return;
    }

    addTimeOffRequest(timeOffRequestPayload)
      .then((res) => res.json())
      .then((result) => {
        toggleAreYouSureDialog(false);
        setIsSubmitButtonClicked(false);
        setIsTimeOffRequestSubmitted(false);
        setOpenSuccessDialog(true);
      })
      .catch((err) => {
        toggleAreYouSureDialog(false);
        setIsSubmitButtonClicked(false);
        setIsTimeOffRequestSubmitted(false);
        console.error("Exception adding Time Off Request", err);
      });
  };

  const getConfirmationMessage = () => {
    if (isEditTimeOffRequest) {
      return "Are you sure you want to remove the shift?";
    }
    if (isSubmitButtonClicked) {
      if (timeOffRequestsList?.length > 1) {
        return ARE_YOU_SURE_WANT_TO_CREATE_THE_TIME_OFF_REQUESTS;
      } else {
        return ARE_YOU_SURE_WANT_TO_CREATE_THE_TIME_OFF_REQUEST;
      }
    } else {
      return isEditTimeOffRequest
        ? ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.EDITS)
        : ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.DATA);
    }
  };

  useEffect(() => {
    setHasUnknownShift(false);
    for (let i = 0; i < timeOffRequestsList?.length; i++) {
      if (
        isEADorEDUorSEAD(timeOffRequestsList[i].timeOffType) &&
        isTimeUnknownForShift(
          timeOffRequestsList[i].shiftStartDateTime.c.hour,
          timeOffRequestsList[i].shiftStartDateTime.c.minute,
          timeOffRequestsList[i].shiftEndDateTime.c.hour,
          timeOffRequestsList[i].shiftStartDateTime.c.minute
        )
      ) {
        setHasUnknownShift(true);
        break;
      }
    }
  }, [timeOffRequestsList]);

  const handleNoClickedCancelDialog = () => {
    toggleAreYouSureDialog(false);
    setIsSubmitButtonClicked(false);
  };

  const renderCancelAreYouSureDialog = () => {
    return (
      <>
        {!isEditTimeOffRequest && !isSubmitButtonClicked && (
          <ConfirmationDialog
            isDialogOpen={isAreYouDialogOpen}
            message={getConfirmationMessage()}
            handleNoButtonClicked={handleNoClickedCancelDialog}
            redirectTo={props.featureConfig.adminPath}
            hasWarning={hasUnknownShift && isSubmitButtonClicked}
            warningMessage={
              timeOffRequestsList?.length === 1
                ? WARNING_ONE_REQUEST_HAS_NO_SHIFT_TIME
                : WARNING_MULTIPLE_REQUESTS_HAVE_NO_SHIFT_TIME
            }
            warningTitle={WARNING_ALERT_TITLE}
          />
        )}
        {(isEditTimeOffRequest || isSubmitButtonClicked) && (
          <ConfirmationDialog
            isDialogOpen={isAreYouDialogOpen}
            message={getConfirmationMessage()}
            handleNoButtonClicked={handleNoClickedCancelDialog}
            handleYesButtonClicked={handleOnYesButtonClick}
            isYesLoading={isTimeOffRequestSubmitted}
            hasWarning={hasUnknownShift && isSubmitButtonClicked}
            warningMessage={
              timeOffRequestsList?.length === 1
                ? WARNING_ONE_REQUEST_HAS_NO_SHIFT_TIME
                : WARNING_MULTIPLE_REQUESTS_HAVE_NO_SHIFT_TIME
            }
            warningTitle={WARNING_ALERT_TITLE}
          />
        )}
      </>
    );
  };

  const noTimeOffRequestMessage = () => {
    return <div className={classes.noTimeOffRequestMessage}>No Time Off Requested</div>;
  };

  const handleOnSubmitValidation = () => {
    let flag = true;
    const response = timeOffRequestsList?.map((ele) => {
      const status = TOR_validateShiftTwoMonthDeadline(
        ele?.shiftStartDateTime,
        ele?.shiftStartDateTime?.hour,
        ele?.shiftStartDateTime?.minute,
        station
      );
      if (status) {
        ele.isExpired = true;
      } else {
        ele.isExpired = false;
      }
      return status;
    });
    setTimeOffRequestsList(timeOffRequestsList);
    const expiredList = response?.filter((ele) => ele !== "");
    if (expiredList?.length > 0) {
      handleWarningDialogBox("At least 1 shift is greater than 2 months in the past!");
      flag = false;
    }
    const commentsValidationStatus = validateAdminComments(comments);
    if (commentsValidationStatus) {
      setCommentsTouched(true);
      setCommentsErrors(commentsValidationStatus);
      flag = false;
    }
    return flag;
  };
  const handleWarningDialogBox = (message) => {
    setWarningDialogBox(!warningDialogBox);
    setWarningDialogBoxMessage(message);
  };

  const renderStationMenuItem = (stationName) => {
    return (
      <MenuItem
        key={stationName}
        value={stationName}
        disabled={!props.featureConfig.userStations.includes(stationName)}
      >
        {stationName}
      </MenuItem>
    );
  };

  const CancelButtonEmployeeId = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={handleEmployeeDataClear}>
        <CancelIcon />
      </IconButton>
    );
  };

  const CancelButtonComments = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={handleCommentsClear}>
        <CancelIcon />
      </IconButton>
    );
  };
  const breadcrumbsList = [
    BREADCRUMBS_TITLE.HOME,
    BREADCRUMBS_TITLE.TIME_OFF_ADMINISTRATION,
    BREADCRUMBS_TITLE.CREATE_TIME_OFF,
  ];
  const breadcrumbsLinks = [HOME_PATH, props.featureConfig.adminPath];
  const confirmationRequired = () => {
    if (employeeId !== "") {
      return true;
    } else {
      return false;
    }
  };
  const renderDesktopAdminCreateTimeOffPage = () => {
    return (
      <>
        <StarsBreadcrumbs
          breadcrumbsList={breadcrumbsList}
          breadcrumbsLinks={breadcrumbsLinks}
          isConfirmationRequired={confirmationRequired()}
          cancelActionType={CANCEL_WARNING_MESSAGE_ACTION_TYPE.DATA}
        />
        <div className={classes.root}>
          <div
            className={
              !props.isDesktopView
                ? `${classes.pageTitle} page-title-left`
                : `${classes.pageTitleDesktop} page-title-left`
            }
          >
            {" "}
            Create Time Off
          </div>
          <Divider className={!props.isDesktopView ? classes.divider : `divider ${classes.topDivider}`} />
          <Grid className={classes.inputContainer} container>
            <Grid item xs={5}>
              {userSession.appRole === MULTI_STATION_ADMIN_ACCESS ? (
                <FormControl variant="outlined">
                  <InputLabel>Station</InputLabel>
                  <Select
                    id="stationSelectElement"
                    className={classes.textInput}
                    value={station}
                    onChange={handleStationChange}
                    onBlur={handleStationBlur}
                    label="Station"
                    error={stationTouched && stationErrors}
                    disabled={timeOffRequestsList?.length > 0 || isAddTimeOffDialogOpen}
                  >
                    <MenuItem value="" disabled>
                      <em>Station</em>
                    </MenuItem>
                    {getMultiStationAdminStationList(userSession)
                      .slice()
                      .sort()
                      .map((station) => {
                        return renderStationMenuItem(station);
                      })}
                  </Select>
                  {stationTouched && stationErrors ? <FormHelperText error>{stationErrors}</FormHelperText> : ""}
                </FormControl>
              ) : (
                <TextField
                  className={props.isDesktopView ? "text-field" : classes.stationTextField}
                  label="Station"
                  variant="outlined"
                  value={station}
                  disabled
                />
              )}
            </Grid>
            <Grid item xs={4}>
              <Grid container>
                <Grid item xs={6}>
                  <TextField
                    className={classes.textInput}
                    label="Employee ID"
                    variant="outlined"
                    value={employeeId}
                    onChange={handleEmployeeIdChange}
                    onBlur={handleEmployeeIdBlur}
                    onKeyUp={handleEmployeeIdEnterKeyPressed}
                    InputProps={{
                      endAdornment:
                        employeeId !== "" && timeOffRequestsList?.length < 1 && !isAddTimeOffDialogOpen ? (
                          <InputAdornment position="start">
                            <CancelButtonEmployeeId />
                          </InputAdornment>
                        ) : (
                          ""
                        ),
                    }}
                    error={employeeIdErrors !== ""}
                    helperText={employeeIdErrors}
                    disabled={timeOffRequestsList?.length >= 1 || isAddTimeOffDialogOpen}
                  />
                </Grid>
                <Grid item xs={6}>
                  {employeeIdSearchTriggered ? (
                    employeeIdSearchLoaded ? (
                      employeeIdErrors === "" && employeeData ? (
                        <Grid className={classes.employeeInfoText} item xs={12}>
                          <div>
                            <span className="bold-text">Name:</span>{" "}
                            {employeeData.lastName + ", " + employeeData.firstName}
                          </div>
                          {employeeData.tempStation && (
                            <div>
                              <span className="bold-text">Temp Station:</span> {employeeData.tempStation} (Active)
                            </div>
                          )}
                          <div>
                            <span className="bold-text">Home Station:</span> {employeeData.homeStation}
                          </div>
                          <div>
                            <span className="bold-text">Workgroup:</span>{" "}
                            {getWorkgroupGivenJobCode(employeeData.jobCode)}
                          </div>
                        </Grid>
                      ) : (
                        ""
                      )
                    ) : (
                      <CircularProgress className={classes.employeeInfoLoading} />
                    )
                  ) : (
                    ""
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Divider className={!props.isDesktopView ? classes.divider : `divider ${classes.bottomDivider}`} />
          <div className={classes.timeOffRequestTable}>
            <div className={innerHtmlWidth ? classes.subHeaderMobile : classes.subHeader}>
              <div className="sub-title"> Time Off Requested</div>
              <Button
                className={innerHtmlWidth ? classes.button : ""}
                variant="contained"
                color="primary"
                onClick={() =>
                  timeOffRequestsList?.length >= 10
                    ? handleWarningDialogBox("You cannot create more than 10 shifts on one form!")
                    : setIsAddTimeOffDialogOpen(!isAddTimeOffDialogOpen)
                }
                disabled={!employeeData || employeeIdErrors !== ""}
              >
                <AddIcon className="button-icon" />
                ADD
              </Button>
            </div>
            {props.isDesktopView && timeOffRequestsList.length > 0 ? (
              <TimeOffRequestTable
                timeOffRequestsList={timeOffRequestsList}
                handleOnEditTimeOffRequest={(timeOffRequest) => handleOnEditTimeOffRequest(timeOffRequest)}
              />
            ) : !props.isDesktopView && timeOffRequestsList.length > 0 ? (
              <TimeOffRequestTableMobile
                timeOffRequestsList={timeOffRequestsList}
                handleOnEditTimeOffRequest={(timeOffRequest) => handleOnEditTimeOffRequest(timeOffRequest)}
                isDesktopView={props.isDesktopView}
              />
            ) : (
              noTimeOffRequestMessage()
            )}
          </div>

          <div className={classes.actionButtons}>
            <Box className="comments-box">
              <TextField
                error={commentsTouched && commentsErrors !== ""}
                className="text-field"
                label="Comments"
                variant="outlined"
                value={comments}
                onChange={handleCommentsChange}
                onBlur={handleCommentsBlur}
                helperText={commentsErrors}
                InputProps={{
                  endAdornment:
                    comments !== "" ? (
                      <InputAdornment position="start">
                        <CancelButtonComments />
                      </InputAdornment>
                    ) : (
                      ""
                    ),
                }}
                multiline
              />
              {comments.length > FREEFORM_REASON_CHAR_LIMIT ? (
                <div className="character-limit-label character-limit-label-red">
                  {comments.length} / {FREEFORM_REASON_CHAR_LIMIT}
                </div>
              ) : (
                <div className="character-limit-label">
                  {comments.length} / {FREEFORM_REASON_CHAR_LIMIT}
                </div>
              )}
            </Box>
            <Box m={1}>
              <Button
                className="button"
                variant="outlined"
                color="primary"
                size="large"
                fullWidth={true}
                onClick={() => toggleAreYouSureDialog(true)}
              >
                <Typography variant="h5" component="h2">
                  <Box className="order-selection-text">Cancel</Box>
                </Typography>
              </Button>
            </Box>
            <Box m={1}>
              <Button
                className="button"
                variant="contained"
                color="primary"
                size="large"
                fullWidth={true}
                onClick={() => {
                  if (timeOffRequestsList.length > 0) {
                    if (handleOnSubmitValidation()) {
                      toggleAreYouSureDialog(true);
                      setIsSubmitButtonClicked(true);
                      setCommentsTouched(false);
                      setCommentsErrors("");
                    }
                  } else {
                    handleWarningDialogBox("You must add at least 1 shift!");
                    const commentsValidationStatus = validateAdminComments(comments);
                    if (commentsValidationStatus) {
                      setCommentsTouched(true);
                      setCommentsErrors(commentsValidationStatus);
                    }
                  }
                }}
                disabled={isTimeOffRequestSubmitted}
              >
                {isTimeOffRequestSubmitted ? (
                  <CircularProgress size={24} />
                ) : (
                  <Typography variant="h5" component="h2">
                    <Box className="order-selection-text">Submit</Box>
                  </Typography>
                )}
              </Button>
            </Box>
          </div>
          {employeeData && (isEditTimeOffRequest || isAddTimeOffDialogOpen) && (
            <AddTimeOffRequestDialog
              isAdmin={userSession.isAdmin}
              isCreateTimeOff={true}
              employeeWorkgroup={getWorkgroupGivenJobCode(employeeData.jobCode)}
              employeeJobCode={employeeData.jobCode}
              station={station}
              isDialogOpen={isEditTimeOffRequest ? isEditTimeOffRequest : isAddTimeOffDialogOpen}
              closeAddTimeOff={() => handleCloseAddRequestModal()}
              handleChangeTimeOffTypes={selectTimeOffRequestTimeOff}
              selectedTimeOffType={timeOffRequestObj.current.timeOffType}
              handleOnSave={() => handleOnSave()}
              timeOffTypeError={timeOffTypeError}
              handleShiftStartDateChange={handleShiftStartDateChange}
              handleShiftStartDateHoursChange={handleShiftStartDateHoursChange}
              handleShiftStartDateMinChange={handleShiftStartDateMinChange}
              handleShiftEndDateChange={handleShiftEndDateChange}
              handleShiftEndDateHoursChange={handleShiftEndDateHoursChange}
              handleShiftEndDateMinChange={handleShiftEndDateMinChange}
              shiftStartDate={timeOffRequestObj.current.shiftStartDate}
              shiftStartDateError={startDateError}
              shiftStartDateHours={shiftStartTime.current.shiftStartHour}
              shiftStartDateMin={shiftStartTime.current.shiftStartMin}
              shiftStartTimeError={startTimeError}
              shiftEndDate={timeOffRequestObj.current.shiftEndDate}
              shiftEndDateError={endDateError}
              shiftEndDateHours={shiftEndTime.current.shiftEndHour}
              shiftEndDateMin={shiftEndTime.current.shiftEndMin}
              shiftEndDateTimeError={endTimeError}
              isEdit={isEditTimeOffRequest}
              handleOnRemove={() => toggleAreYouSureDialog(true)}
              isAdminPage={isAdminPage}
              isDesktopView={props.isDesktopView}
              isDSTWarningOpen={isDSTWarningOpen}
              asSoonAsPossibleChecked={asSoonAsPossibleChecked}
              handleAsSoonAsPossibleChecked={handleAsSoonAsPossibleChecked}
              preferredWOPStartHour={preferredWOPStartTime.current.preferredWOPStartHour}
              preferredWOPStartMin={preferredWOPStartTime.current.preferredWOPStartMin}
              preferredWOPStartTimeError={preferredWOPStartTimeError}
              preferredWOPEndHour={preferredWOPEndTime.current.preferredWOPEndHour}
              preferredWOPEndMin={preferredWOPEndTime.current.preferredWOPEndMin}
              preferredWOPEndTimeError={preferredWOPEndTimeError}
              handlePreferredWOPStartHourChange={handlePreferredWOPStartHourChange}
              handlePreferredWOPStartMinChange={handlePreferredWOPStartMinChange}
              handlePreferredWOPEndHourChange={handlePreferredWOPEndHourChange}
              handlePreferredWOPEndMinChange={handlePreferredWOPEndMinChange}
              phoneNumber={phoneNumber}
              handlePhoneNumberChange={handlePhoneNumberChange}
              handlePhoneNumberBlur={handlePhoneNumberBlur}
              phoneNumberErrors={phoneNumberErrors}
            />
          )}
          {warningDialogBox && (
            <WarningDialog
              isDialogOpen={warningDialogBox}
              handleClickDialogClose={() => setWarningDialogBox(!warningDialogBox)}
              header="Warning"
              message={warningDialogBoxMessage}
              severity={"error"}
            />
          )}
        </div>

        {renderCancelAreYouSureDialog()}
        {openSuccessDialog && (
          <SuccessDialog
            isSuccessDialogOpen={openSuccessDialog}
            title={TIME_OFF_CONFIRMATION_TITLE}
            message={
              timeOffRequestsList.length > 1
                ? TOR_MULTIPLE_REQUESTS_CREATED_MESSAGE
                : TOR_SINGLE_REQUEST_CREATED_MESSAGE
            }
            redirectTo={props.featureConfig.adminPath}
          />
        )}
        {isTOREventEligibilityWarningDialogOpen && (
          <WarningDialog
            isDialogOpen={isTOREventEligibilityWarningDialogOpen}
            handleClickDialogClose={() => setIsTOREventEligibilityWarningDialogOpen(false)}
            header="Time Off Request Eligibility"
            message={TIME_OFF_ELIGIBILITY_ADMIN_WARNING}
            severity={"error"}
          />
        )}
      </>
    );
  };

  const renderMobileAdminCreateTimeOffPage = () => {
    return <div className="page-error">{ADMIN_MOBILE_WARNING_MESSAGE}</div>;
  };

  if (!isUserAuthorizedState && !isUserAuthorizedStateLoading) {
    return <UnauthorizedMessage />;
  }
  return (
    <div
      className={`App-contents enable-scroll ${classes.root} ${
        (isAddTimeOffDialogOpen || isEditTimeOffRequest) && classes.pointer
      }`}
      data-testid="admin-create-time-off-page"
      style={{ pointerEvents: isAddTimeOffDialogOpen || isEditTimeOffRequest ? "none" : "auto" }}
    >
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <>{props.isDesktopView ? renderDesktopAdminCreateTimeOffPage() : renderMobileAdminCreateTimeOffPage()}</>
      )}
    </div>
  );
};

export default AdminCreateTimeOffPage;
